import { Link } from 'react-router-dom'
import { ENTITY, PLACE } from '../constants'
import { LEGAL_EFFECTIVE, PRIVACY_EMAIL } from '../legal/meta'
import { LegalLayout, LegalSection } from '../legal/LegalLayout'

export function Privacy() {
  return (
    <LegalLayout
      eyebrow="Legal sheet"
      title="Privacy policy"
      summary="How Passwork FZ LLC collects, uses, stores, and shares personal data when you use passwork.ae, contact us, or subscribe to Compound — including GDPR and related transparency duties."
      documentTitle="Privacy Policy | Passwork Software Solution"
      description="Privacy Policy for Passwork FZ LLC — GDPR-oriented transparency for passwork.ae and Compound."
    >
      <LegalSection code="P-01" title="Controller">
        <p>
          The data controller is {ENTITY}, {PLACE}. Privacy contact:{' '}
          {PRIVACY_EMAIL}. If EU/UK GDPR applies to a processing activity, you
          may also have rights toward us as controller for that activity.
        </p>
      </LegalSection>

      <LegalSection code="P-02" title="Scope">
        <p>
          This Policy covers personal data processed via our public website,
          email and meeting channels, subscription and billing administration
          for Compound, support and training delivery, and related business
          operations. It does not cover third-party IDEs, cloud hosts, or tools
          you connect on your own (for example Cursor); those have their own
          policies.
        </p>
      </LegalSection>

      <LegalSection code="P-03" title="Data we collect">
        <p>Depending on how you interact with us, we may process:</p>
        <ul className="list-none space-y-2">
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Identity and contact data — name, work email, job title, company,
            phone, messaging handles
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Commercial data — subscription plan, invoices, payment references
            (card data is handled by payment processors if used; we do not store
            full card numbers on our servers)
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Support and training records — tickets, session notes, attendees
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Technical data — IP address, browser type, device/OS hints, pages
            viewed, referrer, approximate location derived from IP
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Communications content you send us (project descriptions, files you
            attach). Avoid sending special-category data unless strictly
            necessary and agreed
          </li>
        </ul>
      </LegalSection>

      <LegalSection code="P-04" title="How we collect it">
        <p>
          Directly from you (forms, email, calls, contracts); automatically via
          the site and cookies/similar tech (see our{' '}
          <Link to="/cookies" className="index-link text-ink">
            Cookie Notice
          </Link>
          ); and from public or partner sources when you ask to be introduced
          (for example a referral), limited to business contact details.
        </p>
      </LegalSection>

      <LegalSection code="P-05" title="Purposes and legal bases">
        <p>We process personal data to:</p>
        <ul className="list-none space-y-2">
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Respond to enquiries and provide services — contract or steps prior
            to contract (GDPR Art. 6(1)(b))
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Administer Compound subscriptions, support, and training — contract
            (Art. 6(1)(b))
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Secure the site, prevent abuse, and keep records required by law —
            legitimate interests or legal obligation (Art. 6(1)(f)/(c))
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Improve our site and offerings using aggregated analytics —
            legitimate interests (Art. 6(1)(f)), or consent where required for
            non-essential cookies (Art. 6(1)(a))
          </li>
          <li className="flex gap-3">
            <span className="mt-2.5 h-px w-3 shrink-0 bg-overprint" aria-hidden="true" />
            Send service notices and, where allowed, limited professional
            updates — legitimate interests or consent, with opt-out
          </li>
        </ul>
        <p>
          Where we rely on legitimate interests, we balance them against your
          rights. You may object as described below.
        </p>
      </LegalSection>

      <LegalSection code="P-06" title="Client repositories and project data">
        <p>
          When delivering Compound onboarding or custom work, we may access
          repositories and project systems you authorize. That material is
          processed as your confidential business data under contract. We do
          not use your private source code to train public models. Indexing
          (graph, vector RAG, semantic) runs in environments you control or
          that we provision only under written agreement.
        </p>
      </LegalSection>

      <LegalSection code="P-07" title="Sharing">
        <p>
          We share personal data with processors who help us operate (hosting,
          email, CRM, accounting, payment). They are bound by contracts
          requiring confidentiality and instructions-only processing. We may
          disclose data if required by law, regulation, or valid legal process,
          or to protect rights, safety, and security. We do not sell personal
          data.
        </p>
      </LegalSection>

      <LegalSection code="P-08" title="International transfers">
        <p>
          We are established in the UAE. Data may be processed in the UAE, EEA,
          UK, or other countries where our providers operate. For GDPR-covered
          transfers from the EEA/UK, we use appropriate safeguards such as
          Standard Contractual Clauses or an adequacy decision where available.
        </p>
      </LegalSection>

      <LegalSection code="P-09" title="Retention">
        <p>
          Enquiry data: typically up to 24 months after last contact unless a
          contract follows. Contract and billing records: for the relationship
          plus up to 7 years (or longer if law requires). Support tickets:
          typically 36 months. Server logs: shorter operational windows unless
          needed for security investigations. We delete or anonymize when
          retention ends.
        </p>
      </LegalSection>

      <LegalSection code="P-10" title="Security">
        <p>
          We apply administrative and technical measures appropriate to the
          risk — access control, encryption in transit for public sites,
          least-privilege staff access, and vendor due diligence. No method is
          perfectly secure; please use unique passwords on any portals we
          provide and notify {PRIVACY_EMAIL} of suspected incidents involving
          your data.
        </p>
      </LegalSection>

      <LegalSection code="P-11" title="Your rights">
        <p>
          Depending on applicable law (including GDPR where it applies), you
          may have rights to access, rectify, erase, restrict, object, and data
          portability, and to withdraw consent where processing is
          consent-based. You may lodge a complaint with a supervisory authority
          in your EEA/UK place of residence or work. To exercise rights, email{' '}
          {PRIVACY_EMAIL}. We may need to verify your identity.
        </p>
      </LegalSection>

      <LegalSection code="P-12" title="Children">
        <p>
          Our services are directed to businesses and professionals. We do not
          knowingly collect personal data from children under 16. If you believe
          we have, contact {PRIVACY_EMAIL} and we will delete it.
        </p>
      </LegalSection>

      <LegalSection code="P-13" title="Automated decisions">
        <p>
          We do not make solely automated decisions that produce legal or
          similarly significant effects about individuals who contact the site.
          Compound assists your teams inside your tooling; human review remains
          your responsibility for production outcomes.
        </p>
      </LegalSection>

      <LegalSection code="P-14" title="Changes">
        <p>
          We may update this Policy by posting a revised version with a new
          effective date (currently {LEGAL_EFFECTIVE}). Material changes will
          be highlighted on this page or notified to subscribers.
        </p>
      </LegalSection>
    </LegalLayout>
  )
}
